import { useEffect, useState } from "react";
import api, { formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { isOwner } from "@/lib/roles";
import { Mail, Send, X, AlertTriangle } from "lucide-react";
import { EmailedWarning } from "@/components/EmailedBadge";

const inputCls = "w-full border border-[#15233B]/15 rounded px-2.5 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1E73C9]/30";
const labelCls = "text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60";

const fill = (text, vars) => (text || "").replace(/\{(\w+)\}/g, (m, k) => (vars[k] != null && vars[k] !== "" ? vars[k] : m));

export default function SendEmailDialog({ contact, workspace, sendPath, onClose, onSent }) {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [status, setStatus] = useState(null);
  const [to, setTo] = useState(contact.email || "");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [tplId, setTplId] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/email-templates", { params: { workspace } }).then(({ data }) => setTemplates(data || [])).catch(() => setTemplates([]));
    api.get("/email/status").then(({ data }) => setStatus(data)).catch(() => setStatus({ connected: false }));
  }, [workspace]);

  const vars = {
    first_name: (contact.full_name || "").trim().split(/\s+/)[0] || "there",
    full_name: contact.full_name || "",
    my_name: user?.name || "",
    position: contact.position || "Personal Care Worker",
  };

  const pickTemplate = (id) => {
    setTplId(id);
    const t = templates.find((x) => x.id === id);
    if (t) { setSubject(fill(t.subject, vars)); setBody(fill(t.body, vars)); }
  };

  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(sendPath, { to: to.trim(), subject: subject.trim(), body, template_id: tplId || null });
      toast.success(`Email sent to ${to.trim()}`);
      onSent?.(data);
      onClose();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-[#15233B]/50 flex items-center justify-center p-4" onClick={onClose}>
      <form data-testid="send-email-dialog" onSubmit={send} onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-[#15233B]/10 flex items-center justify-between bg-[#15233B] text-white rounded-t-xl">
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-[#FFCB05]" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#FFCB05]/90">Send email</div>
              <h2 className="text-base font-extrabold leading-tight" data-testid="send-email-contact-name">{contact.full_name}</h2>
            </div>
          </div>
          <button type="button" data-testid="send-email-close" onClick={onClose} className="p-1.5 rounded hover:bg-white/10"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          {status && !status.connected && (
            <div data-testid="send-email-not-connected" className="flex gap-2 text-xs text-[#7a5a1a] bg-[#FFF9C4] border border-[#FFCB05]/50 rounded px-2.5 py-2">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span>Email isn't connected yet. {isOwner(user) ? <a data-testid="gmail-setup-link" href="/settings?tab=integrations" className="font-bold underline">Set up Gmail now →</a> : "Ask an owner to set up Gmail in Settings → Integrations."}</span>
            </div>
          )}
          <EmailedWarning contact={contact} />
          {status?.connected && (
            <div data-testid="send-email-from" className="text-[11px] text-[#15233B]/55">From: <span className="font-semibold text-[#15233B]/80">{status.from}</span></div>
          )}
          <div>
            <label className={labelCls}>To</label>
            <input data-testid="send-email-to" type="email" required value={to} onChange={(e) => setTo(e.target.value)} placeholder="name@example.com" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Template</label>
            <select data-testid="send-email-template" value={tplId} onChange={(e) => pickTemplate(e.target.value)} className={inputCls}>
              <option value="">— write my own —</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Subject</label>
            <input data-testid="send-email-subject" required value={subject} onChange={(e) => setSubject(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Message</label>
            <textarea data-testid="send-email-body" required rows={9} value={body} onChange={(e) => setBody(e.target.value)} className={inputCls} />
          </div>
          <div className="text-[10px] text-[#15233B]/45">The email is logged to this contact's timeline automatically.</div>
        </div>
        <div className="px-5 py-4 border-t border-[#15233B]/10 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-2 rounded text-sm font-semibold text-[#15233B] hover:bg-[#15233B]/5">Cancel</button>
          <button type="submit" data-testid="send-email-submit" disabled={busy || !to.trim() || !subject.trim() || !body.trim()}
            className="flex items-center gap-1.5 px-4 py-2 rounded bg-[#FFCB05] text-[#15233B] text-sm font-extrabold hover:bg-[#FBC02D] disabled:opacity-50 transition-colors">
            <Send className="h-4 w-4" /> {busy ? "Sending…" : "Send email"}
          </button>
        </div>
      </form>
    </div>
  );
}
